import { useTranslation } from 'next-i18next';

import { Suspense } from 'react';
import { useQuery } from 'react-query';

import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

import { LinkItem } from '@components/common/ListItem';
import Loading from '@components/common/Loading';
import Tab, { useTab } from '@components/common/Tab';
import Header from '@components/layout/Header';
import Layout from '@components/layout/Layout';
import List from '@components/layout/List';
import Pad from '@components/layout/Pad';
import SafeArea from '@components/layout/SafeArea';

import { fetchNotices } from '@api/notice';
import { NoticeCategory } from '@constants';
import { withAuth } from '@contexts/auth';
import { formatDate } from '@utils/date';

const NoticeList = ({ category }: { category: NoticeCategory }) => {
  const { data } = useQuery(
    ['notices', category],
    () => fetchNotices(category),
    { staleTime: minutesToMilliseconds(15) }
  );

  return (
    <List border>
      {data?.map((notice) => (
        <li key={`notice-${category}-${notice.id}`}>
          <Pad>
            <LinkItem
              left={
                <>
                  <h6 className="multiline">{notice.title}</h6>
                  <small>{formatDate(notice.created)}</small>
                </>
              }
              to={`/notices/${notice.id}`}
            />
          </Pad>
        </li>
      ))}
    </List>
  );
};

const NoticeListPage = () => {
  const { t } = useTranslation();

  const tab = useTab(
    [
      { label: t('서비스 공지'), value: NoticeCategory.service },
      { label: t('이벤트'), value: NoticeCategory.event },
      { label: t('약관/방침 개정 공지'), value: NoticeCategory.policy },
    ],
    { replace: true }
  );
  const { activeTab } = tab;

  return (
    <Layout>
      <Header title={t('공지사항')} />
      <Tab {...tab} />

      <Suspense fallback={<Loading center />}>
        <SafeArea bottom>
          <NoticeList category={activeTab} />
        </SafeArea>
      </Suspense>
    </Layout>
  );
};

export default withAuth(NoticeListPage, { isSignInRequired: false });
